import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banknote } from 'lucide-react'
import { DashboardLayout } from '../common/page_component/index.js'
import ListPageHeader from '../common/ListPageHeader.js'
import ListFilterPanel from '../common/ListFilterPanel.js'
import SearchSubmitInput from '../common/SearchSubmitInput.js'
import ListPagination from '../common/ListPagination.js'
import Button from '../common/Button.js'
import Alert from '../common/Alert.js'
import Loader from '../common/Loader.js'
import { useAuth } from '../../context/AuthContext'
import amountAdjustmentService from '../../services/amountAdjustmentService.js'
import { shipmentService } from '../../services/shipmentService.js'
import { listIdLinkClass, listIdLinkMobileClass, listPageWrapperClass } from '../common/uiClasses.js'
import { formatIndianDate, formatIndianTime } from '../../utils/localDate.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'

const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const statusClass = (status) => {
  if (status === 'APPROVED') return 'bg-green-100 text-green-800 border-green-200'
  if (status === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200'
  return 'bg-amber-100 text-amber-800 border-amber-200'
}

const filterInputClass = 'w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base'

const AmountAdjustmentList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canReview = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant'
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [status, setStatus] = useState('PENDING')
  const [search, setSearch] = useState('')
  const [userId, setUserId] = useState('')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [review, setReview] = useState(null)
  const [approvedAmount, setApprovedAmount] = useState('')
  const [remark, setRemark] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const response = await amountAdjustmentService.list({
        page,
        per_page: 20,
        status: status || undefined,
        search: search || undefined,
        user_id: canReview && userId ? userId : undefined,
      })
      const data = response.data?.data || []
      setRows((prev) => mergeLoadedPage(page, data, prev))
      setTotalPages(response.data?.pagination?.pages || 1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not load amount requests')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [page, status, search, userId, canReview])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!review) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [review])

  useEffect(() => {
    if (!canReview) return undefined
    let active = true
    shipmentService.getDistinctUsers()
      .then((response) => {
        if (active) setUsers(response.data?.users || [])
      })
      .catch(() => {
        if (active) setUsers([])
      })
    return () => { active = false }
  }, [canReview])

  const openReview = (row) => {
    setReview(row)
    setApprovedAmount(String(row.requested_amount ?? ''))
    setRemark('')
  }

  const approve = async () => {
    const value = Number(approvedAmount)
    if (!review || !Number.isFinite(value) || value <= 0) {
      Alert.error('Enter the amount to save')
      return
    }
    try {
      setSaving(true)
      const response = await amountAdjustmentService.approve(review.request_id, {
        approved_amount: value,
        remark,
      })
      Alert.success(response.data?.message || 'Amount updated')
      setReview(null)
      setRows([])
      if (page === 1) await load()
      else setPage(1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not approve the request')
    } finally {
      setSaving(false)
    }
  }

  const reject = async () => {
    if (!review) return
    if (!remark.trim()) {
      Alert.error('Enter a remark for the agent')
      return
    }
    try {
      setSaving(true)
      const response = await amountAdjustmentService.reject(review.request_id, { remark })
      Alert.success(response.data?.message || 'Request rejected')
      setReview(null)
      setRows([])
      if (page === 1) await load()
      else setPage(1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not reject the request')
    } finally {
      setSaving(false)
    }
  }

  const hasActiveFilters = Boolean(search || userId || status !== 'PENDING')

  const clearFilters = () => {
    setRows([])
    setSearch('')
    setUserId('')
    setStatus('PENDING')
    setPage(1)
  }

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader title="Amount Adjustment" icon={Banknote} className="mb-2 sm:mb-3" />
        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search</label>
            <SearchSubmitInput
              value={search}
              onSearch={(value) => { setRows([]); setSearch(value); setPage(1) }}
              placeholder="Shipment or request id"
              ariaLabel="Search amount requests"
              inputClassName={`list-filter-panel login-input ${filterInputClass}`}
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={status}
              onChange={(event) => { setRows([]); setStatus(event.target.value); setPage(1) }}
              className={filterInputClass}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          {canReview ? (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">User</label>
              <select
                name="user_id"
                value={userId}
                onChange={(event) => { setRows([]); setUserId(event.target.value); setPage(1) }}
                className={filterInputClass}
              >
                <option value="">All Users</option>
                {users.map((person) => (
                  <option key={person.user_id} value={person.user_id}>
                    {person.location_hub_name ? `${person.name} (${person.location_hub_name})` : person.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </ListFilterPanel>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full">
          {loading && page === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message="Loading amount requests..." />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔭</div>
              <p className="text-gray-600 text-lg font-semibold">No amount requests</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full min-w-0 overflow-x-auto">
                <table className="w-full table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                  <thead className="bg-gray-50 rounded-t-xl">
                    <tr className="border-b border-gray-200">
                      <th className="w-[16%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Request</th>
                      <th className="w-[14%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Shipment</th>
                      <th className="w-[14%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Agent</th>
                      <th className="w-[10%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Current</th>
                      <th className="w-[11%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Requested</th>
                      <th className="w-[11%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Approved</th>
                      <th className="w-[16%] px-2 2xl:px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wide">Status</th>
                      <th className="w-[8%] px-2 2xl:px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row, index) => (
                      <tr key={row.request_id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <div className="font-medium text-gray-900 break-words">{row.request_id}</div>
                          <div className="text-xs text-gray-500 whitespace-nowrap">{formatIndianDate(row.created_at)}</div>
                          <div className="text-xs text-gray-500 whitespace-nowrap">{formatIndianTime(row.created_at)}</div>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <button type="button" className={`${listIdLinkClass} break-words text-left`} onClick={() => navigate(`/shipments/view/${encodeURIComponent(row.shipment_id)}`)}>
                            {row.shipment_id}
                          </button>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 break-words">{row.requested_by_name || row.requested_by}</td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">{money(row.current_amount)}</td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">{money(row.requested_amount)}</td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">{row.approved_amount == null ? '—' : money(row.approved_amount)}</td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusClass(row.status)}`}>{row.status}</span>
                          {row.agent_remark ? <p className="text-xs text-gray-500 mt-1 break-words">{row.agent_remark}</p> : null}
                          {row.reviewer_remark ? <p className="text-xs text-gray-600 mt-1 break-words">Remark: {row.reviewer_remark}</p> : null}
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top">
                          {canReview && row.status === 'PENDING' ? (
                            <Button variant="primary" onClick={() => openReview(row)}>Review</Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-3 p-4">
                {rows.map((row) => (
                  <div key={row.request_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 break-all">{row.request_id}</p>
                        <button type="button" className={`${listIdLinkMobileClass} block`} onClick={() => navigate(`/shipments/view/${encodeURIComponent(row.shipment_id)}`)}>
                          #{row.shipment_id}
                        </button>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatIndianDate(row.created_at)}{formatIndianTime(row.created_at) ? ` · ${formatIndianTime(row.created_at)}` : ''}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border shrink-0 ${statusClass(row.status)}`}>{row.status}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="min-w-0 col-span-2">
                        <p className="text-xs text-gray-500 font-medium">Agent</p>
                        <p className="font-semibold text-gray-800 break-words">{row.requested_by_name || row.requested_by}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Current</p>
                        <p className="font-bold text-gray-900">{money(row.current_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Requested</p>
                        <p className="font-bold text-gray-900">{money(row.requested_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Approved</p>
                        <p className="font-bold text-gray-900">{row.approved_amount == null ? '—' : money(row.approved_amount)}</p>
                      </div>
                    </div>
                    {row.agent_remark ? <p className="text-xs text-gray-500 mt-2 break-words">{row.agent_remark}</p> : null}
                    {row.reviewer_remark ? <p className="text-xs text-gray-600 mt-1 break-words">Remark: {row.reviewer_remark}</p> : null}
                    {canReview && row.status === 'PENDING' ? (
                      <Button variant="primary" className="mt-3" onClick={() => openReview(row)}>Review</Button>
                    ) : null}
                  </div>
                ))}
              </div>

              <ListPagination
                currentPage={page}
                totalPages={totalPages}
                loading={loading && page > 1}
                onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              />
            </>
          )}
        </div>
      </div>

      {review ? (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-2 sm:p-4">
          <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : () => setReview(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-4 sm:p-6" role="dialog" aria-modal="true">
            <h2 className="text-lg font-bold text-gray-800">Review amount</h2>
            <p className="text-sm text-gray-600 mt-1 break-all">{review.shipment_id} · {review.request_id}</p>
            <p className="text-sm text-gray-600 mt-2">
              Current {money(review.current_amount)}. Agent asked for {money(review.requested_amount)}. You can save a different amount.
            </p>
            {review.agent_remark ? <p className="text-sm text-gray-700 mt-2 break-words">Agent remark: {review.agent_remark}</p> : null}
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Amount to save</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={approvedAmount}
              onChange={(event) => setApprovedAmount(event.target.value)}
              onWheel={(event) => event.currentTarget.blur()}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Remark</label>
            <textarea
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
              placeholder="Required when rejecting"
            />
            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="secondary" onClick={() => setReview(null)} disabled={saving}>Cancel</Button>
              <Button variant="secondary" onClick={reject} disabled={saving}>Reject</Button>
              <Button variant="primary" onClick={approve} loading={saving} disabled={saving}>Approve</Button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  )
}

export default AmountAdjustmentList
